// Proof of delivery (хүргэлтийн баталгаа): photos, signature, receiver and the coordinator decision
// (verify / reject, ORD_DN_MNG). Shown on the DN detail «Хавсралт» tab and the handover detail.
// Decision buttons stay visible while the proof awaits review; without the permission they are
// aria-disabled with the reason (O95: POD decision reachable again from the DN detail).

import { Anchor, Box, Button, Group, Image, Loader, Modal, SimpleGrid, Stack, Text, Textarea } from "@mantine/core"
import { CheckIcon, XIcon } from "@phosphor-icons/react"
import { ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { DeliveryApi } from "../../apis"
import type { IDeliveryProof } from "../../apis/delivery"
import { dateTimeSecFormat } from "../../utils/date"
import { message } from "../../utils/message"
import { DeliveryActionButton } from "../delivery/action-controls"
import { DotPill, Tone } from "../delivery/atoms"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import classes from "./dn-detail.module.css"

type Props = { deliveryNoteId: string; hideProofMedia?: boolean }

const STATUS_META: Record<IDeliveryProof["verificationStatus"], { tone: Tone; labelKey: string }> = {
  PENDING: { tone: "amber", labelKey: "delivery.pod.status.pending" },
  REQUIRES_REVIEW: { tone: "amber", labelKey: "delivery.pod.status.pending" },
  VERIFIED: { tone: "green", labelKey: "delivery.pod.status.verified" },
  REJECTED: { tone: "red", labelKey: "delivery.pod.status.rejected" },
}

function Fact({ label, children }: Readonly<{ label: string; children: ReactNode }>) {
  return (
    <Stack gap={2}>
      <Text className={classes.label}>{label}</Text>
      {children}
    </Stack>
  )
}

export function PhotoArchivePanel({ deliveryNoteId, hideProofMedia = false }: Props) {
  const { t } = useTranslation(["delivery", "common"])
  const { stateOf } = useDeliveryActions()

  const { data, isLoading, mutate } = useSWR<IDeliveryProof | null>(
    deliveryNoteId ? `/ord/delivery-note/${deliveryNoteId}/proof` : null,
    () => DeliveryApi.getProof(deliveryNoteId),
    { revalidateOnFocus: false },
  )

  const [rejectOpen, setRejectOpen] = useState(false)
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)

  // A missing status (legacy rows) counts as PENDING.
  const status = data?.verificationStatus ?? "PENDING"
  const statusMeta = STATUS_META[status] ?? STATUS_META.PENDING
  const subject = { status }
  const verifyState = stateOf("podVerify", subject)
  const rejectState = stateOf("podReject", subject)

  const onVerify = async () => {
    setSubmitting(true)
    try {
      await DeliveryApi.verifyProof(deliveryNoteId)
      message.success(t("delivery.pod.verifySuccess"))
      await mutate()
    } catch (err) {
      message.error(translateBackendError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const onReject = async () => {
    const trimmed = reason.trim()
    if (trimmed.length < 3) return
    setSubmitting(true)
    try {
      await DeliveryApi.rejectProof(deliveryNoteId, trimmed)
      message.success(t("delivery.pod.rejectSuccess"))
      setRejectOpen(false)
      setReason("")
      await mutate()
    } catch (err) {
      message.error(translateBackendError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const closeReject = () => {
    if (submitting) return
    setRejectOpen(false)
    setReason("")
  }

  return (
    <Box className={classes.attachRow}>
      <Group justify="space-between" align="center" mb="md" wrap="nowrap">
        <Text className={classes.sectionTitle}>{t("delivery.pod.title")}</Text>
        {data && <DotPill tone={statusMeta.tone}>{t(statusMeta.labelKey)}</DotPill>}
      </Group>

      {isLoading ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : !data ? (
        <Text className={classes.hint} ta="center" py="md">
          {t("delivery.pod.empty")}
        </Text>
      ) : (
        <Stack gap="md">
          {verifyState.applies && (
            <Group gap="sm">
              <DeliveryActionButton state={verifyState} variant="default" leftSection={<CheckIcon size={16} />} loading={submitting} onClick={onVerify}>
                {t("delivery.pod.verify")}
              </DeliveryActionButton>
              <DeliveryActionButton
                state={rejectState}
                variant="default"
                leftSection={<XIcon size={16} />}
                disabled={submitting}
                onClick={() => setRejectOpen(true)}
              >
                {t("delivery.pod.reject")}
              </DeliveryActionButton>
            </Group>
          )}

          {status === "REJECTED" && data.rejectReason && (
            <Box className={classes.tile}>
              <Text className={classes.label}>{t("delivery.pod.rejectReason")}</Text>
              <Text fz="var(--nw-fz-body)">{data.rejectReason}</Text>
            </Box>
          )}

          <Group gap="xl" wrap="wrap" align="flex-start">
            <Fact label={t("delivery.pod.receiver")}>
              <Text className={classes.value}>
                {data.receiverName || "-"}
                {data.receiverTitle ? ` · ${data.receiverTitle}` : ""}
              </Text>
              {data.receiverPhone && <Text className={classes.mono}>{data.receiverPhone}</Text>}
            </Fact>
            <Fact label={t("delivery.pod.capturedAt")}>
              <Text className={classes.mono}>{dateTimeSecFormat(data.capturedAt)}</Text>
            </Fact>
            <Fact label={t("delivery.pod.condition")}>
              <DotPill tone={data.packageCondition === "GOOD" ? "green" : data.packageCondition === "DAMAGED" ? "red" : "amber"}>
                {t(`delivery.pod.conditions.${data.packageCondition}`, { defaultValue: "-" })}
              </DotPill>
            </Fact>
            {data.isPartialDelivery && (
              <Fact label={t("delivery.pod.partialLabel")}>
                <DotPill tone="amber">{t("delivery.pod.partial")}</DotPill>
              </Fact>
            )}
          </Group>

          {!hideProofMedia && data.photoUrls.length > 0 && (
            <Stack gap={6}>
              <Text className={classes.label}>
                {t("delivery.pod.photos")} ({data.photoUrls.length})
              </Text>
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs">
                {data.photoUrls.map((url, i) => (
                  <Anchor key={url} href={url} target="_blank" rel="noreferrer">
                    <Image src={url} radius="sm" h={120} fit="cover" alt={t("dnDetail.deliveryPhotoAlt", { n: i + 1 })} />
                  </Anchor>
                ))}
              </SimpleGrid>
            </Stack>
          )}

          {!hideProofMedia && data.signatureDataUrl && (
            <Stack gap={6}>
              <Text className={classes.label}>{t("delivery.pod.signature")}</Text>
              <Box className={classes.tile} maw={320}>
                <Image src={data.signatureDataUrl} h={104} fit="contain" alt={t("delivery.pod.signature")} />
              </Box>
            </Stack>
          )}

          {data.codReceiptPhotoUrl && (
            <Stack gap={6}>
              <Text className={classes.label}>{t("delivery.pod.codReceipt")}</Text>
              <Anchor href={data.codReceiptPhotoUrl} target="_blank" rel="noreferrer" maw={200}>
                <Image src={data.codReceiptPhotoUrl} radius="sm" h={150} fit="cover" alt={t("delivery.pod.codReceipt")} />
              </Anchor>
            </Stack>
          )}
        </Stack>
      )}

      <Modal opened={rejectOpen} onClose={closeReject} title={t("delivery.pod.rejectTitle")} centered>
        <Stack gap="md">
          <Textarea
            label={t("delivery.pod.rejectReason")}
            placeholder={t("delivery.pod.rejectPlaceholder")}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            minRows={3}
            autosize
            required
            error={reason.length > 0 && reason.trim().length < 3 ? t("delivery.pod.rejectReasonShort") : undefined}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeReject} disabled={submitting}>
              {t("common:actions.dismiss")}
            </Button>
            <Button onClick={onReject} loading={submitting} disabled={reason.trim().length < 3}>
              {t("delivery.pod.reject")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
